import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, MapPin, Clock, Package, CheckCircle2 } from "lucide-react";
import { listMyDriverJobs, transitionBooking } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { statusBadgeClass } from "@/lib/constants";
import DriverSiteTime from "@/components/DriverSiteTime";
import { siteTz } from "@/lib/datetime";

export default function DriverDashboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return;
    (async () => {
      const { data, error } = await listMyDriverJobs({ userId: user.id });
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [user?.id, tick]);

  const refresh = () => setTick((k) => k + 1);

  const checkIn = async (row) => {
    setBusyId(row.id);
    const { error } = await transitionBooking({
      bookingId: row.id,
      fromStatus: row.status,
      toStatus: "checked_in",
    });
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Checked in for ${row.reference_number}.`);
    refresh();
  };

  // Today/tomorrow by the job's SITE calendar day, not the phone's.
  const today = [];
  const tomorrow = [];
  const siteDayKey = (iso, tz) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(iso ? new Date(iso) : new Date());
  for (const r of rows) {
    const tz = siteTz(r.site);
    if (siteDayKey(r.scheduled_start, tz) <= siteDayKey(null, tz)) today.push(r);
    else tomorrow.push(r);
  }

  return (
    <div data-testid="driver-dashboard" className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Truck className="h-6 w-6" /> My jobs
        </h1>
        <p className="text-sm text-slate-500">
          Bookings assigned to you for today and tomorrow. Tap a card to check in on site.
        </p>
      </div>

      <Section title="Today" rows={today} busyId={busyId} onCheckin={checkIn} testid="driver-today" />
      <Section title="Tomorrow" rows={tomorrow} busyId={busyId} onCheckin={checkIn} testid="driver-tomorrow" />

      {rows.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-400">
            No jobs scheduled. Drivers see only bookings where they are assigned
            (by user-id) or where the booking&apos;s driver_phone matches their profile phone.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Section({ title, rows, busyId, onCheckin, testid }) {
  if (rows.length === 0) return null;
  return (
    <div data-testid={testid} className="space-y-2">
      <div className="text-sm font-medium text-slate-600">{title} · {rows.length}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <JobCard key={r.id} row={r} busy={busyId === r.id} onCheckin={onCheckin} />
        ))}
      </div>
    </div>
  );
}

function JobCard({ row, busy, onCheckin }) {
  const canCheckIn = ["approved"].includes(row.status);
  const checkedIn = ["checked_in", "loading", "unloading", "completed"].includes(row.status);
  return (
    <Card data-testid={`driver-job-${row.id}`} className="overflow-hidden">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 min-w-0" data-testid={`driver-job-company-${row.id}`}>
            {row.client?.brand_logo_url
              ? <img src={row.client.brand_logo_url} alt="" className="h-6 w-12 rounded object-contain border border-slate-200 bg-white p-0.5" />
              : <Truck className="h-4 w-4 text-slate-400" />}
            <span className="text-xs font-semibold text-slate-700 truncate">{row.client?.name}</span>
            <span className="font-mono text-xs text-slate-500">{row.reference_number}</span>
          </span>
          <Badge className={statusBadgeClass(row.status)}>
            {row.status.replace("_", " ")}
          </Badge>
        </div>
        <div className="text-base font-semibold">
          {row.sender_name || row.receiver_name || "Job"}
        </div>
        <div className="text-xs text-slate-600 space-y-1">
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-slate-400" />
            {row.site?.name} {row.bay?.name ? ` · ${row.bay.name}` : ""}
          </div>
          {row.site?.address_line1 && (
            <div className="text-[11px] text-slate-500 pl-5">{[row.site.address_line1, row.site.city, row.site.state].filter(Boolean).join(", ")}</div>
          )}
          <div className="flex items-start gap-1.5">
            <Clock className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
            <DriverSiteTime start={row.scheduled_start} end={row.scheduled_end} site={row.site} testId={`driver-job-time-${row.id}`} />
          </div>
          {row.vehicle_type && (
            <div className="flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5 text-slate-400" />
              {row.vehicle_type}
            </div>
          )}
        </div>
        <div className="pt-2">
          {checkedIn ? (
            <div className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> Already checked in
            </div>
          ) : canCheckIn ? (
            <Button
              size="sm"
              onClick={() => onCheckin(row)}
              disabled={busy}
              data-testid={`driver-checkin-${row.id}`}
            >
              {busy ? "Checking in…" : "Check in now"}
            </Button>
          ) : ["cancelled", "rejected", "no_show"].includes(row.status) ? (
            <span className="text-xs font-semibold text-rose-700" data-testid={`driver-job-cancelled-${row.id}`}>
              {row.status === "rejected" ? "Not approved" : row.status === "no_show" ? "Marked no-show" : "Cancelled"} — do not attend this slot.
            </span>
          ) : (
            <span className="text-xs text-slate-400">
              Awaiting approval before check-in.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
